// Class-detail dialog: one per page, on <body>, in its own shadow root. Content follows the
// homepage's dialog (tags, title, recurrence, local time, instructor, description, rating
// + reviews) plus the Library's suspended-class banner. No per-class trailer: the only video
// the component plays is "How it works" (openVideo, from the calendar's top bar).

import detailCss from './detail.css?raw';
import {
  DEFAULT_INSTRUCTOR_AVATAR,
  escapeHtml,
  formatResumeDate,
  formatTo12h,
  localTimeOf,
  tagColors,
  timezoneLabel,
} from './schedule-data';
import { loadRatings, ratingRowHtml } from './ratings';
import { lockScroll, unlockScroll } from './scroll-lock';

const CLOSE_ICON =
  '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>';
const ARROW_ICON =
  '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';

let overlay = null;
let card = null;
let content = null;
let closeBtn = null;
let isOpen = false;
let currentKey = null; // slot id of the open dialog; guards the async rating patch
let currentSurface = '';
let returnFocus = null;

function ensureDialog() {
  if (overlay) return;
  const host = document.createElement('div');
  host.setAttribute('data-dmc-live-schedule-detail', '');
  document.body.appendChild(host);
  const root = host.attachShadow({ mode: 'open' });
  root.innerHTML =
    `<style>${detailCss}</style>` +
    '<div class="detail-overlay" aria-hidden="true">' +
    '<div class="detail-card" role="dialog" aria-modal="true" aria-label="Class details">' +
    `<button type="button" class="detail-close" aria-label="Close class details">${CLOSE_ICON}</button>` +
    '<div class="detail-content"></div>' +
    '</div></div>';
  overlay = root.querySelector('.detail-overlay');
  card = root.querySelector('.detail-card');
  content = root.querySelector('.detail-content');
  closeBtn = root.querySelector('.detail-close');

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeDetail();
  });
  closeBtn.addEventListener('click', closeDetail);
  content.addEventListener('click', onContentClick);
}

// playerColor: the account's default is blue; the old trailer popup forced black, so does
// this (Wistia reads embed options from the iframe URL).
function videoHtml(trailerId) {
  return (
    '<div class="detail-video"><iframe src="https://fast.wistia.net/embed/iframe/' +
    encodeURIComponent(trailerId) +
    '?autoPlay=true&playerColor=000000" allow="autoplay; fullscreen" allowfullscreen title="Video"></iframe></div>'
  );
}

function onContentClick(e) {
  const toggle = e.target.closest('.detail-reviews-toggle');
  if (toggle) {
    const list = toggle.closest('.detail-rating').nextElementSibling;
    const open = toggle.getAttribute('aria-expanded') !== 'true';
    toggle.setAttribute('aria-expanded', String(open));
    if (list) list.classList.toggle('is-open', open);
    return;
  }

  // A click inside a shadow root reaches GTM's document listeners retargeted to the host
  // element, so a click trigger on the link never sees it: push the event explicitly.
  if (e.target.closest('.detail-cta a')) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'clicked_get_started_schedule',
      schedule_surface: currentSurface,
    });
  }
}

/* Get Started is offered everywhere except on the pricing pages, where the visitor is
   already choosing a plan: "auto" hides it on any path containing "pricing". "show" /
   "hide" (data-cta) override the rule for a page whose URL does not follow it. Only the
   path is read, so a ?utm_campaign=pricing on the homepage does not hide it. */
export function shouldShowCta(mode) {
  if (mode === 'show') return true;
  if (mode === 'hide') return false;
  return !/pricing/i.test(window.location.pathname);
}

// Explicit data-cta-url > the homepage's own Get Started link (with its EUR variant) > the
// pricing page of the visitor's currency (global.js sets DanceMasterclass.country).
export function pricingUrl(override) {
  if (override) return override;
  let currency = '';
  try {
    currency = String(
      window.DanceMasterclass.country.currency || '',
    ).toLowerCase();
  } catch {
    currency = '';
  }
  try {
    const cta = window.HomepageData.hero.cta.getStarted;
    if (cta && cta.url) {
      if (
        currency === 'eur' &&
        cta.currencyVariants &&
        cta.currencyVariants.eur
      ) {
        return cta.currencyVariants.eur;
      }
      return cta.url;
    }
  } catch {
    // not the homepage
  }
  return currency === 'eur' ? '/pricing-eur' : '/pricing-usd';
}

export function openDetail(cls, inst, opts) {
  if (!cls) return;
  ensureDialog();
  card.classList.remove('detail-card--video');
  currentKey = cls.id;
  currentSurface = opts.surface || '';

  const occ = localTimeOf(cls);
  const start = new Date(occ.isoFull);
  const end = new Date(start.getTime() + (cls.duration || 60) * 60000);
  const endHm =
    String(end.getHours()).padStart(2, '0') +
    ':' +
    String(end.getMinutes()).padStart(2, '0');
  const timeRange = formatTo12h(occ.hour) + ' - ' + formatTo12h(endHm);

  // A suspended class: "Every Thursday" would be a lie while it is paused, so the banner
  // replaces both the tags and the recurrence line.
  const isCanceled = cls.active === false;
  let topHtml;
  let dateHtml = '';
  if (isCanceled) {
    const back = formatResumeDate(cls.resumesOn, 'long');
    topHtml = `<div class="detail-resume">This Class Will Be Back ${escapeHtml(back || 'Soon')}</div>`;
  } else {
    const colors = tagColors(cls.category);
    topHtml =
      '<div class="detail-tags">' +
      `<span class="detail-tag" style="background:${colors[0]};color:${colors[1]}">${escapeHtml(cls.category)}</span>` +
      (cls.level
        ? `<span class="detail-tag" style="background:#7f7f7f;color:#fff">${escapeHtml(cls.level)}</span>`
        : '') +
      '</div>';
    const weekday = start.toLocaleDateString('en-US', { weekday: 'long' });
    const withinWeek = start <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
    dateHtml = withinWeek
      ? `<p class="detail-date">Every ${escapeHtml(weekday)}</p>`
      : '<p class="detail-date detail-date--future">Available starting ' +
        escapeHtml(
          start.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'short',
            day: 'numeric',
          }),
        ) +
        '</p>';
  }

  const tz = timezoneLabel();
  const avatarHtml = inst
    ? '<div class="detail-avatar">' +
      `<img src="${escapeHtml(inst.image_url || DEFAULT_INSTRUCTOR_AVATAR)}" alt="${escapeHtml(inst.firstName)}">` +
      (inst.firstName ? `<span>${escapeHtml(inst.firstName)}</span>` : '') +
      '</div>'
    : '';

  // Description is admin-authored rich text (same trust as the Library), injected as HTML.
  const descHtml = cls.description
    ? `<div class="detail-desc">${cls.description}</div>`
    : '';
  const bodyHtml = descHtml ? '<hr class="detail-rule">' + descHtml : '';

  const ctaHtml = shouldShowCta(opts.cta)
    ? '<div class="detail-cta">' +
      `<a href="${escapeHtml(pricingUrl(opts.ctaUrl))}">Get Started ${ARROW_ICON}</a>` +
      '</div>'
    : '';

  content.innerHTML =
    topHtml +
    '<div class="detail-head"><div>' +
    `<h3 class="detail-title">${escapeHtml(cls.className)}</h3>` +
    dateHtml +
    `<p class="detail-time"><strong>${escapeHtml(timeRange)}</strong> &bull; ${Number(cls.duration) || 0}min</p>` +
    (tz ? `<p class="detail-tz">Local Time (${escapeHtml(tz)})</p>` : '') +
    '</div>' +
    avatarHtml +
    '</div>' +
    bodyHtml +
    ctaHtml +
    ratingRowHtml(cls, inst); // '' if the ratings are not in yet; patched in below

  // The rating row arrives later on a cold load: add it if this same class is still open.
  loadRatings().then(() => {
    if (currentKey !== cls.id || content.querySelector('.detail-rating'))
      return;
    const row = ratingRowHtml(cls, inst);
    if (row) content.insertAdjacentHTML('beforeend', row);
  });

  show(opts.trigger);
}

// The same dialog, holding only a video: the "How it works" button in the calendar's top
// bar. Nothing on the host page is called (the pricing page's addTrailer, the homepage's
// openVideoModal), so it plays the same on every surface.
export function openVideo(trailerId, opts) {
  ensureDialog();
  currentKey = null;
  currentSurface = opts.surface || '';
  card.classList.add('detail-card--video');
  content.innerHTML = videoHtml(trailerId);
  show(opts.trigger);
}

function show(trigger) {
  if (!isOpen) {
    isOpen = true;
    // The element that opened it lives in another shadow root, so document.activeElement
    // would only name that root's host: the calendar passes the element itself.
    returnFocus = trigger || document.activeElement;
    lockScroll();
    overlay.setAttribute('aria-hidden', 'false');
    // Two frames: commit scale(0.96) before .is-open so it animates in.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        overlay.classList.add('is-open');
        closeBtn.focus({ preventScroll: true });
      }),
    );
  }
  card.scrollTop = 0;
}

export function closeDetail() {
  if (!isOpen) return false;
  isOpen = false;
  currentKey = null;
  overlay.classList.remove('is-open');
  overlay.setAttribute('aria-hidden', 'true');
  // Stop a playing video now rather than when the next class replaces the content.
  content.querySelectorAll('.detail-video').forEach((v) => v.remove());
  unlockScroll();
  if (returnFocus && typeof returnFocus.focus === 'function') {
    returnFocus.focus({ preventScroll: true });
  }
  return true;
}
